import { ensureFreshToken, refreshAndRetry } from '../features/auth/tokenRefresh';

const PLATFORM_ENDPOINT =
  import.meta.env.VITE_PLATFORM_ENDPOINT ?? 'http://localhost:3010';

async function fetchOnce(path: string, init: RequestInit, token: string | null): Promise<Response> {
  return fetch(`${PLATFORM_ENDPOINT}${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  });
}

async function callOnce<T>(path: string, body: unknown, token: string | null): Promise<T> {
  const res = await fetchOnce(
    path,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
    token,
  );
  if (res.status === 401) {
    throw new Error(`401 unauthorized at ${path}`);
  }
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`platform-service ${path} failed: ${res.status} ${txt}`);
  }
  return res.json();
}

async function call<T>(path: string, body: unknown): Promise<T> {
  const token = await ensureFreshToken();
  try {
    return await callOnce<T>(path, body, token);
  } catch (err) {
    return refreshAndRetry(err, (newToken) => callOnce<T>(path, body, newToken));
  }
}

async function getOnce<T>(path: string, token: string | null): Promise<T> {
  const res = await fetchOnce(path, { method: 'GET' }, token);
  if (res.status === 401) {
    throw new Error(`401 unauthorized at ${path}`);
  }
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`platform-service ${path} failed: ${res.status} ${txt}`);
  }
  return res.json();
}

async function get<T>(path: string): Promise<T> {
  const token = await ensureFreshToken();
  try {
    return await getOnce<T>(path, token);
  } catch (err) {
    return refreshAndRetry(err, (newToken) => getOnce<T>(path, newToken));
  }
}

/* ----------------------------------------------------------------------------
 * Native build trigger. Auto-creates an app_release row tied to the
 * package and dispatches the build. Returns the buildId immediately;
 * the runner callback updates app_release_build's status from
 * `building` -> `ready` (or `failed`).
 *
 * NOTE: there is no separate "publish a package to a release" action.
 * In the Forge model, building IS publishing — the build creates the
 * release row, pins the package, and produces the artifact in one
 * step. The `app_release` table exists purely as a tracking record.
 * -------------------------------------------------------------------------- */

export interface TriggerNativeBuildResult {
  buildId: string;
  releaseId: string;
  runnerTaskId: string;
}

export function triggerNativeBuild(input: {
  packageId: string;
  platform: 'ios' | 'android' | 'all';
  /**
   * When true, the runner signs the Android APK with the bundled
   * default developer keystore. Strictly for testing — installable
   * via `adb install`, not Play Store-publishable.
   */
  useDefaultSigning?: boolean;
}): Promise<TriggerNativeBuildResult> {
  return call<TriggerNativeBuildResult>('/build/native', input);
}

/* ----------------------------------------------------------------------------
 * Android signing keystore — status only (upload is multipart so the
 * AppSettingsScreen calls the REST endpoint directly with FormData).
 * -------------------------------------------------------------------------- */

export interface AndroidKeystoreStatus {
  uploaded: boolean;
  alias?: string;
  filename?: string | null;
  uploadedAt?: string | null;
}

export function getAndroidKeystoreStatus(): Promise<AndroidKeystoreStatus> {
  return get<AndroidKeystoreStatus>('/build/credentials/android-keystore/status');
}
